<!--
  Админ-панель для типовых распорядков дня (по ТЗ 03.09.2026).
  Слева — список шаблонов, справа — слоты (интервалы) активного шаблона.
  Один шаблон помечается is_active=True, остальные автоматически «архивируются».
-->
<template>
  <section class="rt">
    <header class="rt__head">
      <h2>{{ $t('routine.title') }}</h2>
      <div class="rt__actions">
        <button class="btn btn--secondary" @click="onAddTemplate">+ {{ $t('routine.addTemplate') }}</button>
      </div>
    </header>

    <div class="rt__body">
      <!-- Левая колонка: список шаблонов -->
      <aside class="rt__side">
        <div v-if="loading" class="rt__state">{{ $t('common.loadingData') }}</div>
        <div v-else-if="!templates.length" class="rt__state">{{ $t('routine.empty') }}</div>
        <div v-else>
          <div
            v-for="t in templates"
            :key="t.id"
            class="tpl"
            :class="{ 'tpl--active': t.is_active, 'tpl--selected': t.id === selectedId }"
            @click="selectTemplate(t.id)"
          >
            <div class="tpl__name">{{ t.name }}</div>
            <div class="tpl__meta">
              <span v-if="t.is_active" class="tpl__badge">{{ $t('routine.activeBadge') }}</span>
              <span>{{ (t.slots || []).length }} {{ $t('routine.slotCount') }}</span>
            </div>
            <div class="tpl__row-actions">
              <button v-if="!t.is_active" class="link" @click.stop="onActivate(t)">{{ $t('routine.setActive') }}</button>
              <button class="link" @click.stop="onRename(t)">{{ $t('common.edit') }}</button>
              <button class="link link--danger" @click.stop="onDelete(t)">{{ $t('common.delete') }}</button>
            </div>
          </div>
        </div>
      </aside>

      <!-- Правая колонка: слоты активного шаблона -->
      <main class="rt__main">
        <div v-if="!selected" class="rt__state">{{ $t('routine.selectHint') }}</div>
        <template v-else>
          <div class="rt__slot-head">
            <h3>{{ selected.name }} — {{ $t('routine.slotsHeader') }}</h3>
            <div class="rt__filters">
              <label>
                {{ $t('routine.weekdayFilter') }}:
                <select v-model.number="weekdayFilter">
                  <option :value="-1">{{ $t('routine.wdAll') }}</option>
                  <option v-for="(l, i) in weekdayLabels" :key="i" :value="i">{{ l }}</option>
                </select>
              </label>
              <button class="btn btn--primary" @click="onAddSlot">+ {{ $t('routine.addSlot') }}</button>
            </div>
          </div>

          <table class="slots">
            <thead>
              <tr>
                <th>{{ $t('routine.wd') }}</th>
                <th>{{ $t('routine.activity') }}</th>
                <th>{{ $t('routine.from') }}</th>
                <th>{{ $t('routine.to') }}</th>
                <th>{{ $t('routine.note') }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!filteredSlots.length"><td colspan="6" class="empty">{{ $t('routine.noSlots') }}</td></tr>
              <tr v-for="slot in filteredSlots" :key="slot.id">
                <td>
                  <select v-model.number="slot.weekday" @change="onSlotChange(slot)">
                    <option :value="-1">{{ $t('routine.wdAll') }}</option>
                    <option v-for="(l, i) in weekdayLabels" :key="i" :value="i">{{ l }}</option>
                  </select>
                </td>
                <td>
                  <select v-model="slot.activity" @change="onSlotChange(slot)">
                    <option v-for="(cfg, key) in ACTIVITIES" :key="key" :value="key">{{ $t(cfg.i18n) }}</option>
                  </select>
                </td>
                <td><input type="time" v-model="slot.start_time" @change="onSlotChange(slot)" /></td>
                <td><input type="time" v-model="slot.end_time" @change="onSlotChange(slot)" /></td>
                <td><input type="text" v-model="slot.note" @change="onSlotChange(slot)" /></td>
                <td>
                  <button class="link link--danger" @click="onDeleteSlot(slot)">✕</button>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </main>
    </div>
  </section>
</template>

<script>
import {
  listRoutineTemplates, createRoutineTemplate, updateRoutineTemplate, deleteRoutineTemplate,
  createRoutineSlot, updateRoutineSlot, deleteRoutineSlot,
} from '@/api/dailyRoutine'
import { ACTIVITIES } from '@/views/chpo/dailySchedule.constants'

export default {
  name: 'RoutineSettingsView',
  data() {
    return {
      loading: false,
      templates: [],
      selectedId: null,
      weekdayFilter: -1,
      ACTIVITIES,
    }
  },
  computed: {
    weekdayLabels() {
      return this.$i18n.locale === 'kz'
        ? ['Дүйсенбі','Сейсенбі','Сәрсенбі','Бейсенбі','Жұма','Сенбі','Жексенбі']
        : ['Понедельник','Вторник','Среда','Четверг','Пятница','Суббота','Воскресенье']
    },
    selected() { return this.templates.find(t => t.id === this.selectedId) || null },
    filteredSlots() {
      if (!this.selected) return []
      const arr = (this.selected.slots || []).slice()
      if (this.weekdayFilter !== -1) return arr.filter(s => s.weekday === -1 || s.weekday === this.weekdayFilter)
      return arr.sort((a, b) => {
        if (a.weekday !== b.weekday) return a.weekday - b.weekday
        return (a.start_time || '').localeCompare(b.start_time || '')
      })
    },
  },
  async mounted() { await this.reload() },
  methods: {
    async reload(keepId) {
      this.loading = true
      try {
        this.templates = await listRoutineTemplates()
        if (keepId != null) this.selectedId = keepId
        else if (!this.selectedId && this.templates.length) {
          const active = this.templates.find(t => t.is_active)
          this.selectedId = (active || this.templates[0]).id
        }
      } catch (e) { console.error('routine load', e) }
      finally { this.loading = false }
    },
    selectTemplate(id) { this.selectedId = id },

    async onAddTemplate() {
      const name = prompt(this.$t('routine.newNamePh'))
      if (!name) return
      try {
        const created = await createRoutineTemplate({ name, is_active: this.templates.length === 0 })
        await this.reload(created.id)
      } catch (e) { alert(this.$t('routine.saveError')); console.error(e) }
    },
    async onRename(t) {
      const name = prompt(this.$t('routine.renamePh'), t.name)
      if (!name || name === t.name) return
      try { await updateRoutineTemplate(t.id, { name }); await this.reload(t.id) }
      catch (e) { alert(this.$t('routine.saveError')); console.error(e) }
    },
    async onActivate(t) {
      try { await updateRoutineTemplate(t.id, { is_active: true }); await this.reload(t.id) }
      catch (e) { alert(this.$t('routine.saveError')); console.error(e) }
    },
    async onDelete(t) {
      if (!confirm(this.$t('routine.deleteConfirm', { name: t.name }))) return
      try {
        await deleteRoutineTemplate(t.id)
        if (this.selectedId === t.id) this.selectedId = null
        await this.reload()
      } catch (e) { alert(this.$t('routine.saveError')); console.error(e) }
    },

    async onAddSlot() {
      if (!this.selected) return
      const payload = {
        template: this.selected.id,
        weekday: this.weekdayFilter === -1 ? -1 : this.weekdayFilter,
        activity: 'personal',
        start_time: '12:00:00',
        end_time: '13:00:00',
        note: '',
      }
      try { await createRoutineSlot(payload); await this.reload(this.selected.id) }
      catch (e) { alert(this.$t('routine.saveError')); console.error(e) }
    },
    async onSlotChange(slot) {
      try {
        await updateRoutineSlot(slot.id, {
          weekday: slot.weekday,
          activity: slot.activity,
          start_time: slot.start_time,
          end_time: slot.end_time,
          note: slot.note,
        })
      } catch (e) { alert(this.$t('routine.saveError')); console.error(e) }
    },
    async onDeleteSlot(slot) {
      if (!confirm(this.$t('routine.deleteSlotConfirm'))) return
      try { await deleteRoutineSlot(slot.id); await this.reload(this.selected.id) }
      catch (e) { alert(this.$t('routine.saveError')); console.error(e) }
    },
  },
}
</script>

<style scoped>
.rt { padding: 20px 24px; }
.rt__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.rt__head h2 { margin: 0; font-size: 20px; }
.rt__actions { display: flex; gap: 8px; }
.rt__body { display: grid; grid-template-columns: 320px 1fr; gap: 20px; }
.rt__side { background: #fff; border: 1px solid #E5E7EB; border-radius: 10px; padding: 8px; max-height: 80vh; overflow: auto; }
.rt__main { background: #fff; border: 1px solid #E5E7EB; border-radius: 10px; padding: 16px; }
.rt__state { padding: 20px; text-align: center; color: #6B7280; }

.tpl { padding: 10px 12px; border-radius: 8px; cursor: pointer; margin-bottom: 4px; border: 1px solid transparent; }
.tpl:hover { background: #F9FAFB; }
.tpl--selected { background: #EFF6FF; border-color: #93C5FD; }
.tpl__name { font-weight: 600; font-size: 14px; color: #111827; }
.tpl__meta { display: flex; gap: 8px; font-size: 12px; color: #6B7280; margin-top: 2px; }
.tpl__badge { background: #D1FAE5; color: #065F46; padding: 1px 6px; border-radius: 4px; font-weight: 700; font-size: 10px; }
.tpl__row-actions { display: flex; gap: 6px; margin-top: 6px; }
.link { background: none; border: 0; color: #2563EB; cursor: pointer; font-size: 12px; padding: 2px 4px; }
.link:hover { text-decoration: underline; }
.link--danger { color: #DC2626; }

.rt__slot-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; gap: 12px; flex-wrap: wrap; }
.rt__slot-head h3 { margin: 0; font-size: 16px; }
.rt__filters { display: flex; gap: 8px; align-items: center; }

.slots { width: 100%; border-collapse: collapse; font-size: 13px; }
.slots th { text-align: left; background: #F3F4F6; padding: 8px; border-bottom: 1px solid #D1D5DB; font-weight: 600; color: #374151; }
.slots td { padding: 6px 8px; border-bottom: 1px solid #F3F4F6; vertical-align: middle; }
.slots select, .slots input { padding: 4px 6px; border: 1px solid #D1D5DB; border-radius: 4px; font-family: inherit; font-size: 13px; width: 100%; }
.slots input[type="time"] { width: 110px; }
.empty { text-align: center; color: #9CA3AF; padding: 20px; }

.btn { padding: 8px 12px; border-radius: 8px; border: 1px solid transparent; cursor: pointer; font-weight: 600; font-size: 13px; font-family: inherit; }
.btn--secondary { background: #fff; border-color: #D1D5DB; color: #374151; }
.btn--primary { background: #2F78EE; color: #fff; }
</style>
